import _ from 'underscore';

import FormattersConstants from 'notebook/mediocre/formatted-text/FormattersConstants';
import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {CustomColumnDataType} from 'notebook/structured-tables/custom-columns/CustomColumnConstants';
import {LastSyncedUpstreamValue} from 'notebook/structured-tables/StructuredTableConstants';
import {getLastSyncedUpstreamValueWithColumnConstraint} from 'notebook/structured-tables/util/getLastSyncedUpstreamValue';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {TableColumnType, ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {selectionStraddlesFreezeBoundary} from 'notebook/tables/core-ui/FreezeColumnUtils';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {computeVirtualRowIndex} from 'notebook/tables/store/logic/TableMergedCellLogic';
import {computeIsRealIndex} from 'notebook/tables/store/logic/TableSelectionLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {AlignmentState} from 'notebook/tables/toolbar/FormulaBox';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import {Optional} from 'util/Types';

/**
 * The link (url + text) for the currently active cell, surfaced in the formula
 * bar / table controls. Matches the shape returned by the original
 * `TableEditable.getSelectionLink()`, which returned an empty object when there
 * is no linkable selection.
 */
export interface SelectionLink {
  url?: Optional<string>;
  text?: string;
}

/**
 * Computes whether the "add link" button should be disabled.
 *
 * This mirrors the original `TableEditable.linkButtonDisabled()`: a link may only
 * be set when exactly one cell is selected, i.e. the selection's start and end
 * indices are both defined and refer to the same cell.
 *
 * @param multiStartIndex - The anchor index where the selection started
 * @param multiEndIndex - The index where the selection ended
 */
export function computeLinkButtonDisabled(
  multiStartIndex: TableIndex | null,
  multiEndIndex: TableIndex | null
): boolean {
  return !(
    multiStartIndex != null &&
    multiEndIndex != null &&
    multiStartIndex[0] === multiEndIndex[0] &&
    multiStartIndex[1] === multiEndIndex[1]
  );
}

/**
 * Computes the link (url + text) for the active cell.
 *
 * This mirrors the original `TableEditable.getSelectionLink()`: a link is only
 * returned when there is a real selection, the link button is enabled (exactly
 * one cell selected), and a selection anchor exists. Otherwise an empty object
 * is returned.
 *
 * @param hasRealSelection - Whether there is a real (in-bounds) selection
 * @param linkButtonDisabled - Whether the link button is disabled (see
 *   `computeLinkButtonDisabled`)
 * @param multiStartIndex - The anchor index of the current selection
 * @param table - The table whose cell link/text should be read
 */
export function computeSelectionLink(
  hasRealSelection: boolean,
  linkButtonDisabled: boolean,
  multiStartIndex: TableIndex | null,
  table: Table
): SelectionLink {
  if (!hasRealSelection || linkButtonDisabled || multiStartIndex == null) {
    return {};
  }
  const [row, column] = multiStartIndex;
  return {
    url: table.table[row][column].link,
    text: table.table[row][column].text,
  };
}

/**
 * Computes the common precision across all rendered cells in the current
 * selection.
 *
 * This mirrors the original `TableEditable.getSelectionPrecision()`: it returns
 * the shared precision value when every rendered selected cell has the same
 * precision, `null` when there is no real selection, and `null` when the
 * selected cells have differing precisions.
 *
 * @param table - The table whose cell precisions should be read
 * @param computedTable - The evaluated table, used to skip non-rendered cells
 * @param expandedBox - The selection box (expanded to contain merged regions)
 * @param hasRealSelection - Whether there is a real (in-bounds) selection
 */
export function computeSelectionPrecision(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>,
  expandedBox: MultiSelectionBox | null,
  hasRealSelection: boolean
): Optional<number> {
  if (!hasRealSelection || expandedBox == null) {
    return null;
  }
  const {minI, maxI, minJ, maxJ} = expandedBox;
  let precision: Optional<number> = null;
  for (let i = minI; i <= maxI; i++) {
    for (let j = minJ; j <= maxJ; j++) {
      if (TableUtil.isNotRendered(i, j, computedTable[i][j])) {
        continue;
      }
      if (precision != null && precision !== table.table[i][j].precision) {
        return null;
      }
      if (precision == null) {
        ({precision} = table.table[i][j]);
      }
    }
  }

  return precision;
}

/**
 * Computes the common `format` value across all rendered selected cells.
 *
 * This mirrors the original `TableEditable.getSelectionFormatting()`: it returns
 * the shared value when every rendered selected cell agrees, or `undefined` when
 * there is no real selection or the values differ. Header cells are treated as
 * bold when computing the `bold` format.
 *
 * @param table - The table whose cells should be read
 * @param computedTable - The evaluated table, used to skip non-rendered cells
 * @param expandedBox - The selection box (expanded to contain merged regions)
 * @param hasRealSelection - Whether there is a real (in-bounds) selection
 * @param format - Either a cell property name or a reducer over (cell, evaluatedCell)
 */
export function computeSelectionFormatting<T extends string | boolean | number>(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>,
  expandedBox: MultiSelectionBox | null,
  hasRealSelection: boolean,
  format: string | ((cell: Cell, evaluatedCell: EvaluatedCell) => T)
): T | undefined {
  const keyFn = _.iteratee(format);
  if (!hasRealSelection || expandedBox == null) {
    return;
  }
  let value = null;
  const {minI, maxI, minJ, maxJ} = expandedBox;
  for (let i = minI; i <= maxI; i++) {
    for (let j = minJ; j <= maxJ; j++) {
      if (TableUtil.isNotRendered(i, j, computedTable[i][j])) {
        continue;
      }
      const cell = table.table[i][j];
      const evaluatedCell = computedTable[i][j];
      // If we are a header and it was not already explicitly false, skip.
      if (
        format === FormattersConstants.BOLD &&
        ((i === 0 && table.headerRow) || (j === 0 && table.headerCol))
      ) {
        value = true;
        // Else if this is the first loop, set the value to match against
      } else if (i === minI && j === minJ) {
        value = keyFn(cell, evaluatedCell);
        // Else if the current value doesn't match the previous, return null
      } else if (value !== keyFn(cell, evaluatedCell)) {
        return;
      }
    }
  }
  return value;
}

/**
 * Computes the alignment state for the current selection.
 *
 * This mirrors the original `TableEditable.getSelectionAlignment()`: `active` is
 * the shared explicit alignment across the selection (or `undefined` when cells
 * disagree), and `default` is the shared rendered alignment, falling back to
 * `'left'`.
 *
 * @param table - The table whose cells should be read
 * @param computedTable - The evaluated table, used to skip non-rendered cells
 * @param expandedBox - The selection box (expanded to contain merged regions)
 * @param hasRealSelection - Whether there is a real (in-bounds) selection
 */
export function computeSelectionAlignment(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>,
  expandedBox: MultiSelectionBox | null,
  hasRealSelection: boolean
): AlignmentState {
  return {
    active: computeSelectionFormatting(
      table,
      computedTable,
      expandedBox,
      hasRealSelection,
      FormattersConstants.ALIGN
    ),
    default:
      computeSelectionFormatting(table, computedTable, expandedBox, hasRealSelection, (cell, evaluatedCell) =>
        TableUtil.alignCell(cell, evaluatedCell)
      ) ?? 'left',
  };
}

/**
 * Computes whether the current selection contains only datetime cells.
 *
 * This mirrors the original `TableEditable._isSelectionOnlyDatetimeCells()`: it
 * returns `true` only when there is a real selection and every selected column
 * is a datetime column (a TAG_VALUE column whose schema field is a datetime, or
 * a CUSTOM column whose data type is datetime). Otherwise it returns `false`.
 *
 * @param columnConstraints - The table's per-column constraints
 * @param expandedBox - The selection box (expanded to contain merged regions)
 * @param hasRealSelection - Whether there is a real (in-bounds) selection
 */
export function computeIsSelectionOnlyDatetimeCells(
  columnConstraints: Array<ColumnConstraint | null>,
  expandedBox: MultiSelectionBox | null,
  hasRealSelection: boolean
): boolean {
  // At least one, and only real cells are selected
  if (!hasRealSelection || expandedBox == null) {
    return false;
  }
  // If any column is not for a datetime, disallow inserting now.
  const {minJ, maxJ} = expandedBox;
  for (let j = minJ; j <= maxJ; j++) {
    const constraint = columnConstraints[j];
    if (
      !(
        constraint?.columnDefinition?.type === TableColumnType.TAG_VALUE &&
        constraint?.columnDefinition?.schemaField.fieldType === FIELD_TYPES.DATETIME.type
      ) &&
      !(
        constraint?.columnDefinition?.type === TableColumnType.CUSTOM &&
        constraint?.columnDefinition?.customColumnConfig.dataType === CustomColumnDataType.DATETIME
      )
    ) {
      return false;
    }
  }
  return true;
}

/**
 * The state of the "merge cells" button in the table controls. Matches the shape
 * returned by the original `TableEditable._mergeButtonFormatting()`: `active`
 * indicates the button is toggled on (selection is a single rendered merged
 * cell), and `disabled` (when present) indicates the button is not actionable.
 */
export interface MergeButtonFormatting {
  active: boolean;
  disabled?: boolean;
}

/**
 * Computes the merge-button formatting state for the current selection.
 *
 * This mirrors the original `TableEditable._mergeButtonFormatting()`:
 *   - With no real selection, the button is neither active nor disabled.
 *   - The button is disabled when the selection starts inside the single-input
 *     region, straddles the freeze boundary, or (for multi-cell selections) when
 *     `allowMergeSelection` rejects the range.
 *   - The button is active only when a single already-merged cell is selected.
 *
 * @param hasRealSelection - Whether there is a real (in-bounds) selection
 * @param multiStartIndex - The anchor index of the current selection
 * @param multiEndIndex - The index where the selection ended
 * @param singleInputBoundaryIndex - The first non-single-input column index, or
 *   `null` when there are no single-input columns
 * @param freezeBoundary - The table's absolute freeze boundary column index
 * @param computedTable - The evaluated table, used to detect merged cells
 * @param allowMergeSelection - Optional predicate gating multi-cell merges
 */
export function computeButtonFormatting(
  hasRealSelection: boolean,
  multiStartIndex: TableIndex | null,
  multiEndIndex: TableIndex | null,
  singleInputBoundaryIndex: number | null,
  freezeBoundary: number,
  computedTable: Array<Array<EvaluatedCell>>,
  allowMergeSelection?: (multiStartIndex: TableIndex, multiEndIndex: TableIndex) => boolean
): MergeButtonFormatting {
  if (!hasRealSelection || multiStartIndex == null || multiEndIndex == null) {
    return {active: false};
  }

  if (singleInputBoundaryIndex && multiStartIndex[1] < singleInputBoundaryIndex) {
    return {active: false, disabled: true};
  }

  const {minI, minJ, maxJ} = TableUtil.indicesToCorners(multiStartIndex, multiEndIndex);
  if (selectionStraddlesFreezeBoundary(minJ, maxJ, freezeBoundary)) {
    return {active: false, disabled: true};
  }

  if (compareTableIndex(multiStartIndex, multiEndIndex) !== 0) {
    if (allowMergeSelection) {
      return {active: false, disabled: !allowMergeSelection(multiStartIndex, multiEndIndex)};
    }
    return {active: false};
  }

  const evaluatedCell = computedTable[minI][minJ];
  if (TableUtil.isRenderedMergedCell(minI, minJ, evaluatedCell)) {
    return {active: true};
  }
  return {active: false, disabled: true};
}

/**
 * The values the floating cell editor renders with for the cell being edited.
 */
export interface ActiveCellEditorModel {
  /**
   * The row index the editor renders at. For a merged cell this is the first row of the merge
   * box, since a merge only renders at its top-left cell.
   */
  virtualRowIndex: number;
  /** The column index of the edited cell. */
  colIndex: number;
  /** The last value synced from upstream for the edited cell's column, when there is one. */
  lastSyncedUpstreamValue: LastSyncedUpstreamValue | null;
}

/**
 * Computes the cell editor values for the cell at [rowIndex, colIndex], or `null` when the editor
 * should not render: the row has not been evaluated yet, or the index is outside the table.
 */
export function computeActiveCellEditor({
  rowIndex,
  colIndex,
  isRowEvaluated,
  isRealIndex,
  evaluatedCell,
  rowData,
  columnConstraint,
}: {
  rowIndex: number;
  colIndex: number;
  isRowEvaluated: boolean;
  isRealIndex: boolean;
  evaluatedCell: EvaluatedCell | undefined;
  rowData: RowData | null;
  columnConstraint: ColumnConstraint | null;
}): ActiveCellEditorModel | null {
  if (!isRowEvaluated || !isRealIndex) {
    return null;
  }
  return {
    virtualRowIndex: computeVirtualRowIndex(rowIndex, evaluatedCell?.mergedBox),
    colIndex,
    lastSyncedUpstreamValue: getLastSyncedUpstreamValueWithColumnConstraint(
      rowData?.lastSyncedUpstreamValues,
      getColumnConstraintWithRowData(columnConstraint, rowData)
    ),
  };
}

/**
 * Computes the cell editor values for the cell at `index`, reading the cell's data from the table.
 */
export function computeActiveCellEditorAtIndex(
  index: TableIndex,
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>
): ActiveCellEditorModel | null {
  const [rowIndex, colIndex] = index;
  return computeActiveCellEditor({
    rowIndex,
    colIndex,
    isRowEvaluated: computedTable[rowIndex] != null,
    isRealIndex: computeIsRealIndex(index, table),
    evaluatedCell: computedTable[rowIndex]?.[colIndex],
    rowData: table.rowData?.[rowIndex] ?? null,
    columnConstraint: table.columnConstraints[colIndex],
  });
}

/**
 * Orders two table indices by row then column. Mirrors the inline
 * `TableEditable.compareIndex` comparator.
 */
function compareTableIndex(a: TableIndex, b: TableIndex): number {
  return a[0] - b[0] || a[1] - b[1];
}
